import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import Sidebar from '../../Components/Sidebar/Sidebar';
import verificandoApp from '../../../FuncoesGerais/verificaApp';
import verificandoToken from '../../../FuncoesGerais/VerificarToken';
import { fetchRagEstatisticas, fetchRagChunks, reindexarRag, fetchRagVerificar } from '../../../api/rag';
import './baseRag.css';

function BaseRag() {
  const history = useHistory();
  const [estatisticas, setEstatisticas] = useState({ chamados: 0, base_conhecimento: 0, sprints: 0, total: 0 });
  const [chunks, setChunks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reindexando, setReindexando] = useState(false);
  const [verificando, setVerificando] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [reindexChamados, setReindexChamados] = useState(true);
  const [reindexBase, setReindexBase] = useState(true);
  const [reindexSprints, setReindexSprints] = useState(true);
  const [incrementalChamados, setIncrementalChamados] = useState(true);

  const verificar = async () => {
    const resultado = await verificandoApp('ATENDENTE');
    if (!resultado) history.push('/');
  };

  const load = async () => {
    verificandoToken();
    setLoading(true);
    setMensagem('');
    try {
      const [resultStats, resultChunks] = await Promise.allSettled([
        fetchRagEstatisticas(),
        fetchRagChunks({ limit: 200, tipo: filtroTipo || undefined }),
      ]);
      if (resultStats.status === 'fulfilled') {
        const v = resultStats.value || {};
        setEstatisticas({
          chamados: v.chamados ?? 0,
          base_conhecimento: v.base_conhecimento ?? 0,
          sprints: v.sprints ?? 0,
          total: v.total ?? 0,
          empresa_id: v.empresa_id,
        });
      } else {
        setEstatisticas({ chamados: 0, base_conhecimento: 0, sprints: 0, total: 0 });
        setMensagem(resultStats.reason?.response?.data?.error || 'Erro ao carregar estatísticas do índice.');
      }
      if (resultChunks.status === 'fulfilled') {
        setChunks(Array.isArray(resultChunks.value) ? resultChunks.value : []);
      } else {
        setChunks([]);
        const msgChunks = resultChunks.reason?.response?.data?.error || 'Erro ao listar índice.';
        setMensagem((prev) => (prev ? `${prev} ${msgChunks}` : msgChunks));
      }
    } catch (e) {
      setMensagem(e?.response?.data?.error || 'Erro ao carregar dados do índice.');
      setChunks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    verificar();
    load();
  }, [filtroTipo]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVerificar = async () => {
    verificandoToken();
    setVerificando(true);
    setMensagem('');
    try {
      const res = await fetchRagVerificar();
      if (res?.ok) {
        setMensagem(
          `Conexão OK. Modelo: ${res.modelo || '—'} | Chave configurada: sim | Dimensões do embedding: ${res.dimensoes || '—'}`
        );
      } else {
        setMensagem(
          `Verificação falhou: ${res?.erro || 'erro desconhecido'}${res?.modelo ? ` (modelo: ${res.modelo})` : ''}`
        );
      }
    } catch (e) {
      setMensagem(e?.response?.data?.erro || e?.message || 'Erro ao verificar conexão com a API de embedding.');
    } finally {
      setVerificando(false);
    }
  };

  const handleReindexar = async () => {
    if (!reindexChamados && !reindexBase && !reindexSprints) {
      setMensagem('Marque ao menos uma opção para reindexar.');
      return;
    }
    verificandoToken();
    setReindexando(true);
    setMensagem('');
    try {
      const res = await reindexarRag({
        chamados: reindexChamados,
        base_conhecimento: reindexBase,
        sprints: reindexSprints,
        incremental: incrementalChamados,
      });
      setMensagem(res?.message || 'Reindexação concluída.');
      await load();
      await new Promise((r) => setTimeout(r, 400));
      await load();
    } catch (e) {
      const errMsg = e?.response?.data?.error || 'Erro ao reindexar.';
      const detalhe = e?.response?.data?.detalhe;
      setMensagem(detalhe ? `${errMsg} Detalhe: ${detalhe}` : errMsg);
    } finally {
      setReindexando(false);
    }
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content base-rag-page">
        <div className="container-fluid py-3">
          <h1 className="tacoma-page-title">Base para IA</h1>
          <p className="text-muted mb-3">
            Índice usado pelo assistente Tacoma (chat e sugestões): chamados fechados, base de conhecimento e sprints. Você pode visualizar o que está indexado e reindexar quando quiser.
            {estatisticas.empresa_id != null && (
              <span className="ms-2">· Empresa (ID: {estatisticas.empresa_id})</span>
            )}
          </p>

          <div className="row mb-4">
            <div className="col-md-3">
              <div className="card base-rag-card">
                <div className="card-body">
                  <h5 className="card-title text-primary">Chamados fechados</h5>
                  <p className="card-text display-6">{loading ? '—' : estatisticas.chamados}</p>
                  <small className="text-muted">indexados na empresa</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card base-rag-card">
                <div className="card-body">
                  <h5 className="card-title text-info">Base de conhecimento</h5>
                  <p className="card-text display-6">{loading ? '—' : estatisticas.base_conhecimento}</p>
                  <small className="text-muted">artigos indexados</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card base-rag-card">
                <div className="card-body">
                  <h5 className="card-title text-success">Sprints</h5>
                  <p className="card-text display-6">{loading ? '—' : estatisticas.sprints}</p>
                  <small className="text-muted">sprints indexadas</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card base-rag-card">
                <div className="card-body">
                  <h5 className="card-title">Total</h5>
                  <p className="card-text display-6">{loading ? '—' : estatisticas.total}</p>
                  <small className="text-muted">chunks no índice</small>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
              <span className="text-muted small fw-medium">Reindexar:</span>
              <label className="form-check form-check-inline mb-0">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={reindexChamados}
                  onChange={(e) => setReindexChamados(e.target.checked)}
                />
                <span className="form-check-label">Chamados fechados</span>
              </label>
              <label className="form-check form-check-inline mb-0">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={reindexBase}
                  onChange={(e) => setReindexBase(e.target.checked)}
                />
                <span className="form-check-label">Base de conhecimento</span>
              </label>
              <label className="form-check form-check-inline mb-0">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={reindexSprints}
                  onChange={(e) => setReindexSprints(e.target.checked)}
                />
                <span className="form-check-label">Sprints</span>
              </label>
              {reindexChamados && (
                <label className="form-check form-check-inline mb-0" title="Incremental: só indexa chamados que ainda não têm chunk (recomendado para não sobrecarregar a IA)">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={incrementalChamados}
                    onChange={(e) => setIncrementalChamados(e.target.checked)}
                  />
                  <span className="form-check-label text-muted small">Incremental (chamados)</span>
                </label>
              )}
            </div>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleVerificar}
                disabled={verificando || reindexando}
                title="Testa se a API de embedding (Gemini) está acessível no servidor"
              >
                {verificando ? 'Verificando...' : 'Verificar conexão'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleReindexar}
                disabled={reindexando || (!reindexChamados && !reindexBase && !reindexSprints)}
              >
                {reindexando ? 'Reindexando...' : 'Reindexar selecionados'}
              </button>
            </div>
          </div>
          <div className="mb-3 d-flex flex-wrap align-items-center gap-2">
            <select
              className="form-select form-select-sm w-auto"
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="chamado">Chamados</option>
              <option value="base_conhecimento">Base de conhecimento</option>
              <option value="sprint">Sprints</option>
            </select>
          </div>

          {mensagem && (
            <div
              className={`alert ${
                mensagem.indexOf('Erro') !== -1 || mensagem.indexOf('Verificação falhou') !== -1
                  ? 'alert-danger'
                  : mensagem.indexOf('nenhum chamado foi indexado') !== -1 || mensagem.indexOf('GEMINI_API_KEY') !== -1
                  ? 'alert-warning'
                  : 'alert-success'
              }`}
              role="alert"
            >
              {mensagem}
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Itens indexados (amostra)</h5>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="p-4 text-center text-muted">Carregando...</div>
              ) : chunks.length === 0 ? (
                <div className="p-4 text-center text-muted">
                  Nenhum item no índice. Use &quot;Reindexar tudo&quot; para popular.
                  {estatisticas.total > 0 && (
                    <div className="mt-2 small text-warning">
                      Há {estatisticas.total} itens indexados mas a lista está vazia. Recarregue a página (F5) ou troque de empresa no seletor e volte.
                    </div>
                  )}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Referência</th>
                        <th>Trecho</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chunks.map((c) => (
                        <tr key={c.id}>
                          <td>
                            <span
                              className={`badge ${
                                c.tipo === 'chamado'
                                  ? 'bg-primary'
                                  : c.tipo === 'sprint'
                                    ? 'bg-success'
                                    : 'bg-info'
                              }`}
                            >
                              {c.tipo === 'chamado' ? 'Chamado' : c.tipo === 'sprint' ? 'Sprint' : 'Artigo'}
                            </span>
                          </td>
                          <td>
                            {c.tipo === 'chamado' ? `#${c.referencia_id}` : c.tipo === 'sprint' ? `Sprint ID ${c.referencia_id}` : `ID ${c.referencia_id}`}
                          </td>
                          <td className="base-rag-trecho">{c.trecho || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default BaseRag;
